import * as React from "react";
import { useCallback, useEffect } from "react";
import { ImageArcGISRest, XYZ, Vector as VectorSource } from "ol/source";
import { Image as ImageLayer, Tile as TileLayer, Vector as VectorLayer } from "ol/layer";
import TileGrid from "ol/tilegrid/TileGrid";
import { PopupBody, PopupHeader } from "./Popup";
import { useAppContext } from "../Contexts/AppContext";
import { Checkbox } from "../Elements/Checkbox";
import { config, ILayerConfig, ILegend } from "../Map/config";
import { CloseButton } from "../Elements/CloseButton";
import { getMap } from "../Forms/DrawingForm";
import classNames from "classnames";
import "./LayersMenu.scss";

export const LayersMenu = ({ visible }: { visible: boolean }): JSX.Element => {
    const { isMobile, enabledLayers, setEnabledLayers, toggleLayersMenuVisible } = useAppContext();

    const map = getMap();

    useEffect(() => {
        const layers = localStorage.getItem("pubEventsLayers")
            ? JSON.parse(localStorage.getItem("pubEventsLayers"))
            : config.layers.map((layer) => layer.visible && layer.showInToc && layer.id).filter((l) => l);

        const enabled = layers.filter((layer) => layer != undefined);
        setEnabledLayers(enabled);
    }, []);

    useEffect(() => {
        config.layers.map((layerConfig) => {
            const mapLayer = map
                .getLayers()
                .getArray()
                .find((layer) => layer.get("id") === layerConfig.id);

            enabledLayers.includes(layerConfig.id)
                ? !mapLayer && map.addLayer(prepareLayer(layerConfig))
                : map.removeLayer(mapLayer);
        });
    }, [enabledLayers]);

    const toggleLayer = useCallback(
        (visible: boolean, layerId: string): void => {
            const cleanBeforeAdd = enabledLayers.filter((id) => id !== layerId);

            setEnabledLayers(visible ? [...cleanBeforeAdd, layerId] : cleanBeforeAdd);
            saveLayersSettings(visible ? [...cleanBeforeAdd, layerId] : cleanBeforeAdd);
        },
        [enabledLayers]
    );

    return visible ? (
        <div className="shadow" id="layers-popup">
            {isMobile ? (
                <PopupHeader isMobile={isMobile}>
                    <CloseButton onClose={toggleLayersMenuVisible}></CloseButton>
                </PopupHeader>
            ) : (
                <div className="triangle-left"></div>
            )}
            <PopupBody isMobile={isMobile}>
                {config.layers.map((layer) => {
                    return (
                        <div key={"layer-" + layer.id}>
                            <Checkbox
                                label={layer.title}
                                subTitle={"© " + layer.copyright}
                                defaultChecked={enabledLayers.includes(layer.id)}
                                onChange={(e) => toggleLayer(e, layer.id)}
                            ></Checkbox>
                            {layer.legend &&
                                layer.legend.map((legend) => <Legend key={legend.title} legend={legend} />)}
                        </div>
                    );
                })}
            </PopupBody>
        </div>
    ) : null;
};

const Legend = ({ legend }: { legend: ILegend }) => {
    return (
        <div className="legend">
            <div
                className={classNames("legend-symbol", { "has-border": !!legend.borderColor })}
                style={{
                    backgroundColor: legend.fillColor,
                    outlineColor: legend.borderColor
                }}
            ></div>
            <div className="legend-title">{legend.title}</div>
        </div>
    );
};

const prepareLayer = (layerConfig: ILayerConfig) => {
    if (layerConfig.type === "TileXYZ") {
        return new TileLayer({
            source: new XYZ({
                url: layerConfig.url + "/tile/{z}/{y}/{x}",
                projection: config.default.projection,
                crossOrigin: layerConfig.crossOrigin,
                tileGrid: new TileGrid(config.default.tileGrid)
            }),
            minResolution: layerConfig.minResolution,
            maxResolution: layerConfig.maxResolution,
            extent: layerConfig.extent,
            properties: { id: layerConfig.id },
            zIndex: layerConfig.zIndex
        });
    }
    if (layerConfig.type === "ImageArcGISRest") {
        return new ImageLayer({
            properties: { id: layerConfig.id },
            source: new ImageArcGISRest({
                url: layerConfig.url,
                crossOrigin: layerConfig.crossOrigin,
                attributions: layerConfig.copyright,
                params: layerConfig.params,
                imageLoadFunction: (image, src) =>
                    ((image.getImage() as HTMLImageElement).src = src.replace("DPI=90", "DPI=96")),
                ratio: 1
            }),
            zIndex: layerConfig.zIndex
        });
    }
};

const saveLayersSettings = (storedLayers) => {
    const storage = localStorage.getItem("pubEventsLayers");
    if (storage && storage === JSON.stringify(storedLayers)) {
        return;
    }
    localStorage.setItem("pubEventsLayers", JSON.stringify(storedLayers));
};
